<template>
  <v-form ref="formValid" v-model="valid">
    <v-card
      class="dat-container"
      @drop.prevent="onDrop($event)"
      @dragover.prevent="dragover = true"
      @dragenter.prevent="dragover = true"
      @dragleave.prevent="dragover = false"
    >
      <v-card-text class="content d-flex flex-row">
        <v-col>
          <v-row
            class="d-flex flex-column add-file-container pa-7 mb-0"
            :class="{ 'error-file': !isValidFile() }"
            dense
            align="center"
            justify="center"
          >
            <v-icon class="upload-file" size="45" rounded>
              mdi-file-upload-outline
            </v-icon>
            <p>
              {{ $t('map.tolldomains.geometry.drop') }}
            </p>
            <v-file-input
              id="add-dat-file-input"
              ref="fileInputValidation"
              v-model="uploaded"
              class="file-input"
              :color="colors.secondary"
              hide-details
              outlined
              rounded
              dense
              :placeholder="$t('map.tolldomains.geometry.choose')"
              :rules="datfileRules"
              prepend-icon=""
              @change="onFileChange"
            >
              <template v-slot:selection="{}">
                {{ $t('map.tolldomains.geometry.choose') }}
              </template>
            </v-file-input>
          </v-row>
          <v-row v-if="!isValidFile()" class="error-file-text">
            <v-col v-for="(rule, index) in datfileRules" :key="index" class="pt-2 pb-0">
              {{ rule(getFile()) }}
            </v-col>
          </v-row>
        </v-col>
        <v-col>
          <v-row class="file-name-input d-flex flex-column">
            <div class="mb-1 mt-4 file-name">{{ $t('map.tolldomains.dats.list.header_filename') }}</div>
            <v-text-field
              id="add-dat-name"
              class="file-name-input"
              outlined
              dark
              dense
              :value="uploaded ? uploaded.name : ''"
              disabled
              hide-details
            />
          </v-row>
          <v-row class="file-name-input d-flex flex-column">
            <div class="mb-1 file-name">{{ $t('map.tolldomains.dats.list.header_uid') }}</div>
            <v-text-field
              id="add-dat-uid"
              v-model="fileData.uid"
              class="text-field-uid"
              disabled
              outlined
              dark
              dense
              hide-details="auto"
              :rules="[...datuidRules, isUniqueNumber(datids, 'common.error.forms.inputs.validator.datid_unicity')]"
            />
          </v-row>
          <v-row class="file-name-input d-flex flex-column">
            <div class="mb-1 file-name">{{ $t('map.tolldomains.dats.list.header_signature') }}</div>
            <v-text-field
              id="add-dat-signature"
              v-model="fileData.signature"
              disabled
              outlined
              dark
              dense
              hide-details
            />
          </v-row>
        </v-col>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<script lang="ts">
import { Component, Mixins, Prop } from 'vue-property-decorator';
import { colors } from '@/constants/theme/colors';
import Validation from '@/mixins/Validation';
import { MasterAccountInfo } from 'ctp-user-auth-service';

@Component
export default class AddDatDialog extends Mixins(Validation) {
    @Prop()
    private datType!: string;

    @Prop()
    private datnames!: string[];

    @Prop()
    private datids!: number[];

    @Prop()
    private masterAccount!: MasterAccountInfo;

    @Prop()
    private disableAddDat: boolean;

    @Prop()
    private index!: number;

    private datnameRules = [
      this.requiredRule('common.error.forms.inputs.validator.mandatory')
    ];

    private datuidRules = [
      this.requiredRule('common.error.forms.inputs.validator.mandatory')
    ];

    private datfileRules = [
      this.maxFileSize(1032, 'common.error.forms.inputs.validator.datfile_too_large')
    ];

    private colors = colors;

    private dialog = false;

    private uploaded: File|null = null;

    private valid = false;

    private fileData: any = {
      filename: '',
      masterId: '',
      uid: null,
      type: '',
      signature: '',
      fileData: null
    };

    private onDrop(e: any) {
      if (e.dataTransfer.files.length === 1) {
        this.uploaded = e.dataTransfer.files[0];
        this.onFileChange();
      }
    }

    private saveDat() {
      this.fileData.masterId = this.masterAccount.id;
      this.fileData.type = this.datType.toLowerCase();

      this.$emit('saveDat', this.fileData);
    }

    private close() {
      (this.$refs.formValid as HTMLFormElement).reset();
      this.dialog = false;
      this.uploaded = null;
    }

    private onFileChange() {
      if (!this.uploaded) return;
      const reader = new FileReader();
      reader.onload = () => {
        const contents = reader.result;
        const view = new DataView(contents as ArrayBuffer);
        // First 2 bytes (UInt16, offset 0) = File ID
        this.fileData.uid = view.getUint16(0);
        // DWord starting at offset 34 (UInt32, offset 34) = File Signature
        // Convert INT to HEX for display purpose
        this.fileData.signature = view.getUint32(34).toString(16).toUpperCase();
        this.fileData.fileData = this.uploaded;
        this.fileData.fileName = this.uploaded?.name;
        this.fileData.index = this.index;

        this.saveDat();
      };
      reader.readAsArrayBuffer(this.uploaded as File);
    }

    private isValidFile() {
      return this.$refs.fileInputValidation ? (this.$refs.fileInputValidation as HTMLFormElement).validate() : false;
    }

    private getFile() {
      return this.$refs.fileInputValidation ? (this.$refs.fileInputValidation as HTMLFormElement).value : null;
    }
}
</script>

<style lang="scss" scoped>
@import './styles/add-dat.scss';
</style>
